import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { publicAPI } from '../lib/api';
import { ExternalLink, ArrowLeft, Loader2, BookOpen } from 'lucide-react';
import { useT } from '../lib/i18n';
import { useAuth } from '../lib/auth';

// Internal book detail page — LOGGED-IN members only. Anonymous visitors keep the
// public modal (on /reading-list) and are redirected here to that list.
//
// Layout: 2 columns — the book content (2fr, primary) on the left and a sticky
// "Related Insights" rail (1fr, secondary) on the right listing the linked KMS
// posts as a VERTICAL LIST. Below the lg breakpoint the columns stack (content
// first). Styled to the Homepage design language (Plus Jakarta Sans display
// titles, gold accent, sand borders, white surface); each Related Insights link
// hands the session to the KMS via the app-root SSO interceptor, so points fire
// exactly as from any other entry.
const PB_FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Pill CTA — mirrors PBButton (rounded-full; outline fills on hover).
function Pill({ href, children, filled = false, testId }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 999,
    fontFamily: PB_FONT, fontWeight: 600, fontSize: 14, padding: '11px 26px',
    transition: 'all .25s', textDecoration: 'none',
  };
  const style = filled
    ? { ...base, backgroundColor: 'var(--color-button-bg, #11151C)', color: 'var(--color-button-text, #fff)', border: '2px solid var(--color-button-bg, #11151C)' }
    : { ...base, backgroundColor: 'transparent', color: 'var(--color-heading, #11151C)', border: '2px solid var(--color-heading, #11151C)' };
  const hover = (e, on) => {
    if (filled) { e.currentTarget.style.opacity = on ? '0.9' : '1'; return; }
    e.currentTarget.style.backgroundColor = on ? 'var(--color-button-bg, #11151C)' : 'transparent';
    e.currentTarget.style.color = on ? 'var(--color-button-text, #fff)' : 'var(--color-heading, #11151C)';
    e.currentTarget.style.borderColor = on ? 'var(--color-button-bg, #11151C)' : 'var(--color-heading, #11151C)';
  };
  return (
    <a href={href} target="_blank" rel="noreferrer" style={style} data-testid={testId}
      onMouseEnter={e => hover(e, true)} onMouseLeave={e => hover(e, false)}>
      {children}
    </a>
  );
}

function Eyebrow({ children, gold = false }) {
  return (
    <p className="text-[11px] font-semibold uppercase mb-3" style={{ fontFamily: PB_FONT, letterSpacing: '0.25em', color: gold ? 'var(--color-accent, #C9A24B)' : 'var(--color-primary, #000)' }}>
      {children}
    </p>
  );
}

// Related Insights list item — a stacked horizontal mini-card carrying the
// Homepage card DNA (rounded-xl, 1px #E5E7EB border → gold on hover, cover
// thumbnail, Plus Jakarta title #111827 + excerpt #4B5563). The KMS-origin href
// is upgraded to a session hand-off by the app-root SSO interceptor.
function InsightRow({ post, testId }) {
  const hover = (e, on) => {
    e.currentTarget.style.borderColor = on ? '#d3b877' : '#E5E7EB';
    e.currentTarget.style.boxShadow = on ? '0 10px 26px -14px rgba(17,21,28,.2)' : 'none';
  };
  return (
    <a
      href={post.url}
      target="_blank"
      rel="noreferrer"
      className="group flex gap-3.5 p-3 rounded-xl"
      style={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', transition: 'border-color .2s, box-shadow .2s', textDecoration: 'none' }}
      onMouseEnter={e => hover(e, true)}
      onMouseLeave={e => hover(e, false)}
      data-testid={testId}
    >
      <div className="w-24 h-[60px] rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: '#0b1020' }}>
        {post.image
          ? <img src={post.image} alt="" className="w-full h-full object-cover" style={{ filter: 'grayscale(10%)' }} />
          : <BookOpen className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.4)' }} />}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-sm line-clamp-2" style={{ fontFamily: PB_FONT, color: '#111827', lineHeight: 1.35 }}>{post.title}</p>
        {post.summary && <p className="text-xs mt-1 line-clamp-2" style={{ fontFamily: PB_FONT, color: '#4B5563', lineHeight: 1.45 }}>{post.summary}</p>}
      </div>
    </a>
  );
}

export default function BookDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tt = useT();
  const { user, loading: authLoading } = useAuth();
  const isLoggedIn = !!(user && (user.id || user.member_id || user.username || user.email));

  const [book, setBook] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Detail page is members-only: send anonymous visitors back to the public list.
  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) navigate('/reading-list', { replace: true });
  }, [authLoading, isLoggedIn, navigate]);

  useEffect(() => {
    if (!isLoggedIn) return;
    publicAPI.getBook(id).then(r => setBook(r.data)).catch(() => setNotFound(true));
  }, [id, isLoggedIn]);

  if (!isLoggedIn) return null;

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 pt-32 text-center" style={{ fontFamily: PB_FONT }} data-testid="book-detail-notfound">
        <p className="text-slate-500">This book is no longer available.</p>
        <Link to="/reading-list" className="inline-flex items-center gap-2 mt-4 text-sm font-semibold" style={{ color: 'var(--color-accent, #C9A24B)' }}>
          <ArrowLeft className="w-4 h-4" /> Back to Reading List
        </Link>
      </div>
    );
  }

  if (!book) {
    return <div className="p-24 pt-32 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-accent, #C9A24B)' }} /></div>;
  }

  const related = book.related_posts || [];
  const reviews = book.book_reviews || [];
  const Section = ({ title, children }) => (
    <div>
      <Eyebrow gold>{title}</Eyebrow>
      <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: 'var(--color-body-text, #3d434d)' }}>{children}</p>
    </div>
  );

  return (
    <div data-testid="book-detail-page" style={{ fontFamily: PB_FONT, backgroundColor: 'var(--color-bg, #ffffff)', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 pt-24 md:pt-28">
        <Link to="/reading-list" className="inline-flex items-center gap-2 text-sm font-semibold mb-10 hover:opacity-70" style={{ color: 'var(--color-accent, #C9A24B)' }} data-testid="book-detail-back">
          <ArrowLeft className="w-4 h-4" /> Back to Reading List
        </Link>

        {/* 2 columns: book content (2fr) + Related Insights rail (1fr) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">

          {/* ── Main column — book content ── */}
          <div className="lg:col-span-2 min-w-0">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              <img src={book.image} alt={tt(book.title)} className="w-48 h-72 object-cover rounded-md shadow-xl mx-auto md:mx-0 flex-shrink-0" data-testid="book-detail-cover" />
              <div className="min-w-0 pt-1">
                <Eyebrow>Reading List</Eyebrow>
                <h1 style={{ fontFamily: PB_FONT, fontWeight: 900, fontSize: 'clamp(30px, 4vw, 46px)', letterSpacing: '-0.025em', lineHeight: 1.02, color: 'var(--color-heading, #11151C)', margin: 0 }} data-testid="book-detail-title">{tt(book.title)}</h1>
                <p className="text-lg font-semibold mt-3" style={{ color: 'var(--color-accent, #C9A24B)' }}>by {tt(book.author)}</p>
                {tt(book.description) && (
                  <p className="text-[17px] mt-5 leading-relaxed" style={{ color: 'var(--color-body-text, #3d434d)' }}>{tt(book.description)}</p>
                )}
                <div className="flex flex-wrap gap-3.5 pt-7">
                  {book.amazon_link && (
                    <Pill href={book.amazon_link} filled testId="book-detail-amazon">
                      <ExternalLink className="w-4 h-4" /> Buy on Amazon
                    </Pill>
                  )}
                  {book.other_links?.map((link, i) => (
                    <Pill key={i} href={link.url}>
                      <ExternalLink className="w-4 h-4" /> {tt(link.name)}
                    </Pill>
                  ))}
                </div>
              </div>
            </div>

            {/* Long-form sections (same as the modal) */}
            <div className="mt-14 space-y-10 max-w-3xl">
              {tt(book.synopsis) && <Section title="Synopsis">{tt(book.synopsis)}</Section>}
              {tt(book.who_is_it_for) && <Section title="Who Is It For?">{tt(book.who_is_it_for)}</Section>}
              {tt(book.about_author) && <Section title="About the Author">{tt(book.about_author)}</Section>}
            </div>
          </div>

          {/* ── Secondary column — Related Insights (sticky vertical list) ── */}
          <aside className="lg:col-span-1 min-w-0" data-testid="book-related-posts">
            <div className="lg:sticky lg:top-28">
              {/* Book Review — KMS write-ups categorised "Book Reviews" linked to
                  this book, shown ABOVE Related Insights. */}
              {reviews.length > 0 && (
                <div className="mb-9" data-testid="book-review-section">
                  <h2 style={{ fontFamily: PB_FONT, fontWeight: 900, fontSize: 'clamp(22px,2.4vw,26px)', letterSpacing: '-0.015em', lineHeight: 1.05, color: 'var(--color-heading, #11151C)', margin: '0 0 6px' }}>Book Review</h2>
                  <p className="text-[13px] mb-5" style={{ color: 'var(--color-body-text, #6b6459)' }}>Our review of this book from the Knowledge Hub.</p>
                  <div className="flex flex-col gap-3.5">
                    {reviews.map(post => (
                      <InsightRow key={post.id} post={post} testId={`book-review-${post.id}`} />
                    ))}
                  </div>
                </div>
              )}
              <Eyebrow>Knowledge Base</Eyebrow>
              <h2 style={{ fontFamily: PB_FONT, fontWeight: 900, fontSize: 'clamp(22px,2.4vw,26px)', letterSpacing: '-0.015em', lineHeight: 1.05, color: 'var(--color-heading, #11151C)', margin: '0 0 6px' }}>Related Insights</h2>
              <p className="text-[13px] mb-5" style={{ color: 'var(--color-body-text, #6b6459)' }}>Articles from our Knowledge Hub connected to this book.</p>
              {related.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="book-related-empty">No related articles yet.</p>
              ) : (
                <div className="flex flex-col gap-3.5">
                  {related.map(post => (
                    <InsightRow key={post.id} post={post} testId={`book-related-post-${post.id}`} />
                  ))}
                </div>
              )}
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}
